/**
 * Enhanced Error Handling Utilities
 *
 * Provides user-friendly error messages and retry mechanisms
 * for common application errors.
 */

export interface ErrorResult {
  userMessage: string;
  technicalMessage: string;
  isRetryable: boolean;
  suggestedAction?: string;
  errorCode?: string;
  severity: 'error' | 'warning' | 'info';
}

export interface RetryConfig {
  maxAttempts: number;
  delay: number;
  backoffMultiplier: number;
  retryableErrors: string[];
}

export class EnhancedErrorHandler {
  private static defaultRetryConfig: RetryConfig = {
    maxAttempts: 3,
    delay: 1000,
    backoffMultiplier: 2,
    retryableErrors: [
      'network',
      'timeout',
      'rate_limit',
      'server_error',
      'connection_error'
    ]
  };

  /**
   * Convert any error into a user-friendly error result
   */
  static handleError(error: any, context?: string): ErrorResult {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const lowerMessage = errorMessage.toLowerCase();

    // Network and connectivity errors
    if (
      lowerMessage.includes('network') ||
      lowerMessage.includes('fetch') ||
      lowerMessage.includes('connection') ||
      error.code === 'NETWORK_ERROR'
    ) {
      return {
        userMessage: 'Network connection issue. Please check your internet connection and try again.',
        technicalMessage: errorMessage,
        isRetryable: true,
        suggestedAction: 'Check your internet connection and retry',
        errorCode: 'NETWORK_ERROR',
        severity: 'error'
      };
    }

    // Timeout errors
    if (
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('timed out') ||
      error.code === 'TIMEOUT'
    ) {
      return {
        userMessage: 'Request timed out. The server is taking longer than expected to respond.',
        technicalMessage: errorMessage,
        isRetryable: true,
        suggestedAction: 'Wait a moment and try again',
        errorCode: 'TIMEOUT',
        severity: 'warning'
      };
    }

    // Authentication errors
    if (
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('authentication') ||
      lowerMessage.includes('401') ||
      error.response?.status === 401
    ) {
      return {
        userMessage: 'Authentication expired. Please refresh the page and log in again.',
        technicalMessage: errorMessage,
        isRetryable: false,
        suggestedAction: 'Refresh the page and log in again',
        errorCode: 'AUTH_ERROR',
        severity: 'error'
      };
    }

    // Rate limiting
    if (
      lowerMessage.includes('rate limit') ||
      lowerMessage.includes('too many requests') ||
      lowerMessage.includes('429') ||
      error.response?.status === 429
    ) {
      return {
        userMessage: 'Too many requests. Please wait a moment before trying again.',
        technicalMessage: errorMessage,
        isRetryable: true,
        suggestedAction: 'Wait 30 seconds before retrying',
        errorCode: 'RATE_LIMIT',
        severity: 'warning'
      };
    }

    // Server errors
    if (
      lowerMessage.includes('server error') ||
      lowerMessage.includes('internal server') ||
      lowerMessage.includes('500') ||
      error.response?.status >= 500
    ) {
      return {
        userMessage: 'Server error occurred. Our team has been notified and is working on a fix.',
        technicalMessage: errorMessage,
        isRetryable: true,
        suggestedAction: 'Try again in a few minutes',
        errorCode: 'SERVER_ERROR',
        severity: 'error'
      };
    }

    // AI-specific errors
    if (
      lowerMessage.includes('content policy') ||
      lowerMessage.includes('content may violate') ||
      lowerMessage.includes('safety filter')
    ) {
      return {
        userMessage: 'Content triggered safety filters. Try different content or continue with manual metadata.',
        technicalMessage: errorMessage,
        isRetryable: false,
        suggestedAction: 'Use different content or add metadata manually',
        errorCode: 'CONTENT_POLICY',
        severity: 'warning'
      };
    }

    // File upload errors
    if (
      lowerMessage.includes('file too large') ||
      lowerMessage.includes('payload too large') ||
      lowerMessage.includes('413') ||
      error.response?.status === 413
    ) {
      return {
        userMessage: 'File is too large. Please choose a smaller file (under 100MB).',
        technicalMessage: errorMessage,
        isRetryable: false,
        suggestedAction: 'Choose a smaller file and try again',
        errorCode: 'FILE_TOO_LARGE',
        severity: 'error'
      };
    }

    // Validation errors
    if (
      lowerMessage.includes('validation') ||
      lowerMessage.includes('invalid') ||
      lowerMessage.includes('400') ||
      error.response?.status === 400
    ) {
      // Try to extract specific validation message
      const validationMessage = error.response?.data?.message || errorMessage;
      return {
        userMessage: `Validation error: ${validationMessage}`,
        technicalMessage: errorMessage,
        isRetryable: false,
        suggestedAction: 'Check your input and correct any errors',
        errorCode: 'VALIDATION_ERROR',
        severity: 'error'
      };
    }

    // Video/audio processing errors
    if (
      lowerMessage.includes('video processing') ||
      lowerMessage.includes('audio processing') ||
      lowerMessage.includes('media processing')
    ) {
      return {
        userMessage: 'Media processing failed. Try a different format or shorter duration.',
        technicalMessage: errorMessage,
        isRetryable: true,
        suggestedAction: 'Try uploading a different file format',
        errorCode: 'MEDIA_PROCESSING',
        severity: 'warning'
      };
    }

    // Generic error fallback
    return {
      userMessage: context
        ? `${context} failed. Please try again or contact support if the problem persists.`
        : 'An unexpected error occurred. Please try again or contact support if the problem persists.',
      technicalMessage: errorMessage,
      isRetryable: true,
      suggestedAction: 'Try again or contact support',
      errorCode: 'UNKNOWN_ERROR',
      severity: 'error'
    };
  }

  /**
   * Retry mechanism with exponential backoff
   */
  static async withRetry<T>(
    operation: () => Promise<T>,
    config: Partial<RetryConfig> = {},
    onRetry?: (attempt: number, error: any) => void
  ): Promise<T> {
    const finalConfig = { ...this.defaultRetryConfig, ...config };
    let lastError: any;

    for (let attempt = 1; attempt <= finalConfig.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        const errorResult = this.handleError(error);

        // Don't retry if error is not retryable
        if (!errorResult.isRetryable) {
          throw error;
        }

        // Don't retry on last attempt
        if (attempt === finalConfig.maxAttempts) {
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = finalConfig.delay * Math.pow(finalConfig.backoffMultiplier, attempt - 1);

        console.log(`Attempt ${attempt} failed, retrying in ${delay}ms...`, errorResult.userMessage);

        if (onRetry) {
          onRetry(attempt, error);
        }

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }

  /**
   * Format error for display in UI components
   */
  static formatForUI(error: any, context?: string): {
    message: string;
    severity: 'error' | 'warning' | 'info';
    action?: string;
  } {
    const errorResult = this.handleError(error, context);
    return {
      message: errorResult.userMessage,
      severity: errorResult.severity,
      action: errorResult.suggestedAction
    };
  }
}